import { Bot, ExternalLink, AlertTriangle, ArrowUpRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getDiscordIconUrl, buildBotInviteUrl } from "@/utils/discord";
import { useDashboardMe } from "@/lib/api/dashboard";
import { useSubscription } from "@/lib/api/subscription";
import { createGuildAddIntent } from "@/lib/api/guilds";
import { useGuildAddedListener } from "@/hooks/use-guild-added-listener";
import type { ModalComponentProps } from "@/lib/modal";
import type { DiscordGuild, DashboardApplication } from "@/lib/api/types";

export interface AddServerToAppModalProps {
  application: DashboardApplication;
  guilds: DiscordGuild[];
}

export function AddServerToAppModal({
  application,
  guilds,
  onClose,
}: AddServerToAppModalProps & ModalComponentProps) {
  const { data: meData } = useDashboardMe();

  const ownerTenant = meData?.data?.tenants.find((t) => t.role === "owner");
  const tenantId = ownerTenant?.id ?? "";

  const { data: subscriptionData } = useSubscription(tenantId);

  const currentGuilds = subscriptionData?.data?.usage.guilds ?? 0;
  const maxGuilds = subscriptionData?.data?.limits.maxGuildsPerApplication ?? 1;
  const isAtLimit = currentGuilds >= maxGuilds;

  useGuildAddedListener({ onSuccess: onClose });

  const handleAddToGuild = async (guild: DiscordGuild) => {
    try {
      await createGuildAddIntent(application.id, guild.id);
    } catch {
      // Intent creation failed — fall back to manual claim
    }
    window.open(
      buildBotInviteUrl(application.discordClientId, guild.id),
      "_blank"
    );
  };

  return (
    <>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle className="flex items-center gap-2">
          <Bot className="size-5 text-rose-500" />
          Add Server
        </ResponsiveModalTitle>
        <ResponsiveModalDescription>
          Choose a server to add{" "}
          <span className="font-medium">{application.name}</span> to.
        </ResponsiveModalDescription>
      </ResponsiveModalHeader>

      <div className="space-y-4 py-4">
        <div className="bg-muted/50 flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
          <span className="text-muted-foreground">Server Usage</span>
          <span className={isAtLimit ? "text-destructive font-medium" : ""}>
            {currentGuilds} / {maxGuilds}
          </span>
        </div>

        {isAtLimit && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
              <div className="text-sm">
                <p className="font-medium text-amber-600 dark:text-amber-400">
                  Server limit reached
                </p>
                <p className="text-muted-foreground mt-1">
                  Upgrade your plan to add bots to more servers.
                </p>
                {ownerTenant && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-2 h-auto p-0 text-amber-600 dark:text-amber-400"
                    asChild
                  >
                    <Link
                      to="/dashboard/teams/$tenantId"
                      params={{ tenantId: ownerTenant.id }}
                      search={{ tab: "subscription" }}
                    >
                      View subscription
                      <ArrowUpRight className="ml-1 size-3" />
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="max-h-64 space-y-2 overflow-y-auto">
          {guilds.map((guild) => (
            <div
              key={guild.id}
              className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-rose-500/5"
            >
              <Avatar className="size-10">
                <AvatarImage
                  src={getDiscordIconUrl(guild.id, guild.icon)}
                  alt={guild.name}
                />
                <AvatarFallback className="text-xs">
                  {guild.name.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{guild.name}</p>
                {guild.isOwner && (
                  <p className="text-muted-foreground text-xs">Owner</p>
                )}
              </div>
              <Button
                size="sm"
                className="shrink-0 gap-1.5"
                disabled={isAtLimit}
                onClick={() => handleAddToGuild(guild)}
              >
                <ExternalLink className="size-3.5" />
                Add
              </Button>
            </div>
          ))}
        </div>
      </div>

      <ResponsiveModalFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </ResponsiveModalFooter>
    </>
  );
}

AddServerToAppModal.contentClassName = "sm:max-w-md";
AddServerToAppModal.displayName = "AddServerToAppModal";
